import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NavigationBar from "@/components/NavigationBar";
import Footer from "@/components/Footer";
import { useState, useEffect, useRef } from "react";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import ImageModal from "@/components/ImageModal";
import { useCartCount } from "@/hooks/useCartCount";
import ImageWithLoader from "@/components/ImageWithLoader";

// Express Interest Section 1 Component
const ExpressInterestSection1 = ({ user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+91',
    mobile: '',
    state: '',
    city: ''
  });

  // Auto-fill form with user data if logged in
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        mobile: user.phone || '',
        state: user.state || '',
        city: user.city || ''
      });
    }
  }, [user]);

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
    'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep'
  ];

  const handleContinue = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.countryCode || !formData.mobile || !formData.state || !formData.city) {
      toast.error('Please fill all fields');
      return;
    }
    // Store data in sessionStorage and navigate
    sessionStorage.setItem('section1Data', JSON.stringify(formData));
    navigate('/register-interest');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">First Name*</label>
          <Input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData({...formData, firstName: e.target.value})}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Last Name*</label>
          <Input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData({...formData, lastName: e.target.value})}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Email Address*</label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Mobile Number*</label>
        <div className="flex gap-2">
          <select
            value={formData.countryCode}
            onChange={(e) => setFormData({...formData, countryCode: e.target.value})}
            className="w-32 px-3 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
            required
          >
            <option value="+91">🇮🇳 +91</option>
            <option value="+1">🇺🇸 +1</option>
            <option value="+44">🇬🇧 +44</option>
            <option value="+971">🇦🇪 +971</option>
            <option value="+65">🇸🇬 +65</option>
            <option value="+61">🇦🇺 +61</option>
          </select>
          <Input
            type="tel"
            value={formData.mobile}
            onChange={(e) => setFormData({...formData, mobile: e.target.value})}
            placeholder="Enter mobile number"
            className="flex-1"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">State*</label>
        <select
          value={formData.state}
          onChange={(e) => setFormData({...formData, state: e.target.value})}
          className="w-full px-4 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
          required
        >
          <option value="">Select your state</option>
          {indianStates.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">City*</label>
        <Input
          type="text"
          value={formData.city}
          onChange={(e) => setFormData({...formData, city: e.target.value})}
          placeholder="Enter your city"
          required
        />
      </div>

      <Button
        onClick={handleContinue}
        className="w-full bg-white text-gold hover:bg-white/90 font-semibold py-3 sm:py-4 flex items-center justify-center gap-2 shadow-lg text-sm sm:text-base min-h-[44px]"
      >
        Continue
        <ChevronRight size={18} className="sm:w-5 sm:h-5" />
      </Button>
    </div>
  );
};

const HomePage = ({ user }) => {
  const [showNavLogo, setShowNavLogo] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const { cartCount } = useCartCount(user);

  useEffect(() => {
    const handleScroll = () => {
      // Show logo when scrolled past 400px (approximately when main logo section is scrolled past)
      if (window.scrollY > 400) {
        setShowNavLogo(true);
      } else {
        setShowNavLogo(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFBF5' }}>
      {/* Navigation Bar */}
      <NavigationBar user={user} showLogo={showNavLogo} cartCount={cartCount} />

      {/* Logo Section - Opening Animation */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="py-12 sm:py-16 md:py-20 text-center" 
        style={{ minHeight: '40vh', backgroundColor: '#FFFBF5' }}
      >
        <div className="w-full h-full flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ 
              duration: 1, 
              delay: 0.2, 
              ease: [0.6, 0.05, 0.01, 0.9]
            }}
            whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
            className="h-auto w-full max-w-[65%] sm:max-w-[55%] md:max-w-[35%] lg:max-w-[28%] xl:max-w-[22%]"
          >
            <ImageWithLoader
              src="https://customer-assets.emergentagent.com/job_haute-assets/artifacts/jb59wbkx_THC%20Final%20Logo%20%28Cutout%202%29.png" 
              alt="The Haute Circle" 
              className="h-auto w-full object-contain" 
              placeholderColor="#FFFBF5"
            />
          </motion.div>
        </div>
      </motion.section>

      {/* Hero Section - 50/50 Layout */}
      <section className="bg-black">
        <div className="w-full">
          <div className="flex flex-col lg:flex-row">
            {/* Left Side - Text Content (50% width on desktop) */}
            <div className="w-full lg:w-[50%] px-6 py-5 lg:px-[60px] lg:py-20 flex flex-col justify-center order-2 lg:order-1">
              <h1 
                className="text-4xl lg:text-6xl font-bold text-white mb-6 lg:mb-8 uppercase tracking-wide leading-tight" 
                data-testid="hero-heading"
              >
                Elevate your<br />Lifestyle
              </h1>
              
              <p className="text-white/90 mb-6 leading-relaxed text-base font-light first-letter:text-7xl first-letter:font-bold first-letter:float-left first-letter:mr-2 first-letter:font-serif">
                True luxury is in the freedom and ability to experience the best of what the world has to offer, as and when your heart desires.
              </p>
              
              <p className="text-white/90 mb-6 leading-relaxed text-base font-light">
                The Haute Circle is a curator and enabler of luxury lifestyle through fractional ownership of high-value assets. Members pool in capital to own high-value assets such as ultra luxury and super cars, Private Jets and Helicopters, Private Yachts, Vacation Homes, etc, for shared usage. The Haute Circle carefully curates the member groups, facilitates the acquisition, schedule usage of the assets among members, and carry on the management, maintenance, and liquidation of the assets.
              </p>
              
              <p className="text-white/90 mb-10 leading-relaxed text-base font-light">
                The Haute Circle also presents 'The Haute Club', India's first ever 'Automotive' themed luxury lounge and lifestyle club (coming soon) where members and enthusiasts get access to unique amenities that were only available internationally, until now. Members also get invited to some of the most sought after events in the country curated by The Haute Circle. And to make this a truly one-of-a-kind club in the world, it also offers some of the most desirable movable luxury assets to its members as a package to truly live the High Life!<br /><br />The Haute Circle. The Curators of the Ultra High Life.
              </p>
              
              <div>
                <Link to="/how-it-works">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button className="bg-white text-gold hover:bg-white/90 px-6 lg:px-12 py-4 lg:py-6 w-full lg:w-fit text-base lg:text-lg font-semibold shadow-xl transition-all" data-testid="explore-assets-btn">
                      Learn More About Fractional Ownership
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </div>

            {/* Right Side - Image (50% width on desktop) */}
            <div className="w-full lg:w-[50%] relative min-h-[300px] overflow-hidden order-1 lg:order-2">
              <img
                src="https://customer-assets.emergentagent.com/job_hauteassets/artifacts/puu4yhfv_Rolls%20%26%20Copter%20Image.png" 
                alt="Luxury Lifestyle" 
                className="w-full h-full object-cover pointer-events-none select-none absolute inset-0"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6" style={{ backgroundColor: '#FFFBF5' }}>
        <div className="max-w-7xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-3xl sm:text-4xl font-bold text-center mb-8 sm:mb-12 md:mb-16 uppercase tracking-wide"
          >
            Indulgences
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* High Assets Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            >
              <Link to="/high-assets" className="block group" data-testid="high-assets-card">
                <motion.div 
                  className="bg-gradient-to-br from-white to-gray-50 overflow-hidden h-full flex flex-col shadow-md border border-gray-200 cursor-pointer"
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <div className="overflow-hidden">
                    <motion.img 
                      src="https://images.unsplash.com/photo-1698543252507-cd8c187212e2?crop=entropy&cs=srgb&fm=jpg&q=85" 
                      alt="High Assets" 
                      className="w-full h-72 object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                  </div>
                  <div className="p-8 text-center border-t-4 border-black flex-grow flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide">High Assets</h3>
                      <p className="text-gray text-sm mb-4">Ultra-Luxury Vehicles, Private Jets,<br/>Luxury Yachts, Vacation Homes</p>
                    </div>
                    <motion.span 
                      className="inline-block mt-4 text-gold text-sm uppercase font-semibold"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      Explore Now →
                    </motion.span>
                  </div>
                </motion.div>
              </Link>
            </motion.div>

            {/* High Luxury Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            >
              <motion.div 
                className="bg-gradient-to-br from-white to-gray-50 overflow-hidden h-full flex flex-col shadow-md border border-gray-200"
                whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="overflow-hidden">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="w-full h-72"
                  >
                    <ImageWithLoader
                      src="https://customer-assets.emergentagent.com/job_hauteassets/artifacts/p9ocqe0d_Car%20Lounge.png" 
                      alt="High Luxury" 
                      className="w-full h-72 object-cover"
                      placeholderColor="#f9fafb"
                    />
                  </motion.div>
                </div>
                <div className="p-8 text-center border-t-4 border-black flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide">High Luxury</h3>
                    <p className="text-gray text-sm mb-4">An Exclusive Automotive Themed<br/>Club, Lounge, & Cafe</p>
                  </div>
                  <span className="inline-block mt-4 text-gold text-sm uppercase font-semibold">Coming Soon</span>
                </div>
              </motion.div>
            </motion.div>

            {/* High Life Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
            >
              <motion.div 
                className="bg-gradient-to-br from-white to-gray-50 overflow-hidden h-full flex flex-col shadow-md border border-gray-200"
                whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="overflow-hidden">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="w-full h-72"
                  >
                    <ImageWithLoader
                      src="https://customer-assets.emergentagent.com/job_hauteassets/artifacts/9weasu0r_Car%20Event.png" 
                      alt="High Life" 
                      className="w-full h-72 object-cover"
                      placeholderColor="#f9fafb"
                    />
                  </motion.div>
                </div>
                <div className="p-8 text-center border-t-4 border-black flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide">High Life</h3>
                    <p className="text-gray text-sm mb-4">Exclusive Events, Unique Experiences,<br/>& Curated Tours</p>
                  </div>
                  <span className="inline-block mt-4 text-gold text-sm uppercase font-semibold">Coming Soon</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Express Your Interest Section - About You */}
      <section className="py-12 sm:py-16 md:py-20 bg-black">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4 text-white uppercase tracking-wide"
          >
            Express Your Interest
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-center text-white/80 mb-8 sm:mb-12 text-sm sm:text-base"
          >
            We'd love to get to know you before welcoming you to The Haute Circle.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-lg p-4 sm:p-6 md:p-8 shadow-lg"
          >
            <ExpressInterestSection1 user={user} />
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Image Modal */}
      <ImageModal 
        isOpen={!!modalImage}
        onClose={() => setModalImage(null)}
        imageSrc={modalImage}
        alt="Enlarged view"
      />
    </div>
  );
};

export default HomePage;
